import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Target, Map, CheckCircle, Play, ChevronDown, ChevronUp, MessageCircle, Calendar, Sparkles, Lock, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import SEO from '@/components/SEO';
import { toast } from 'sonner';

const LOGO_NAV = "/images/dea-wordmark.png";
const LOGO_HERO = "/images/dea-wordmark.png";
const LOGO_FOOTER = "/images/dea-wordmark.png";
const DEA_AVATAR = "/images/dea-portrait.png";
const DEON_AVATAR = "/images/deon-headshot.png";

// (API constant removed — direct checkout routing is now handled by /checkout?plan=monthly)

// Day cards data
const dayCards = [
  {
    day: 1,
    icon: Target,
    title: "Get Crystal Clear",
    points: [
      "Meet Dea and create your glow up plan — your goals, your dreams, your reinvention journey",
      "Get clear on the ONE area you want to glow up in first",
      "Walk away knowing exactly who you're becoming and why"
    ],
    color: "#CAAB84"
  },
  {
    day: 2,
    icon: Map,
    title: "Build Your Glow Up Journey",
    points: [
      "Get your personalized 90-day glow up roadmap",
      "See your weekly milestones across every area of your life",
      "Receive your first daily action — clear, doable, and made for YOU"
    ],
    color: "#87A6A7"
  },
  {
    day: 3,
    icon: CheckCircle,
    title: "Feel the Difference",
    points: [
      "Show up for your daily check-in with Dea",
      "Notice how different it feels to have someone in your corner",
      "Decide you're not stopping after 7 days — because why would you?"
    ],
    color: "#AA9283"
  },
  {
    day: 4,
    icon: Zap,
    title: "Build the Momentum",
    points: [
      "Your habits are forming — keep your streak alive",
      "Dea adjusts your plan based on what you've accomplished",
      "Notice the difference 4 days of showing up makes"
    ],
    color: "#B8956A"
  },
  {
    day: "5-7",
    icon: Sparkles,
    title: "You Won't Want to Stop",
    points: [
      "Your glow up is already underway",
      "Dea has learned how you work and shows up accordingly",
      "By Day 7 you'll know — this is exactly what you needed"
    ],
    color: "#6B2D3E"
  }
];

// Quiz items
const quizItems = [
  "You've started over so many times you've stopped trusting yourself",
  "You're exhausted but still pushing — because stopping feels like failing",
  "You know what you should be doing. You just can't seem to actually do it",
  "You want to grow your business without losing yourself in the process",
  "You've invested in coaching before and still feel stuck",
  "You're ready to become HER — you just need someone in your corner every single day"
];

// Testimonials for Power Start
const testimonials = [
  {
    quote: "After Day 1, I finally had a goal that made sense. By Day 4, I took action. This is the clarity I needed.",
    author: "Tonya"
  },
  {
    quote: "7 days felt like 7 weeks of progress. Dea built a plan I actually believe in. Now I just need to upgrade!",
    author: "Juliet"
  },
  {
    quote: "Day 1: Cried (good tears). Day 3: Got my task done. Day 7: Realized this is real accountability.",
    author: "Raylin"
  }
];

// FAQ items for Power Start
const faqItems = [
  {
    question: "Do I need ChatGPT?",
    answer: "Nope! Lives on our platform. No ChatGPT needed."
  },
  {
    question: "When does my trial start?",
    answer: "The moment you create your account after paying $9. You'll be talking to Dea within minutes."
  },
  {
    question: "What happens after 7 days?",
    answer: "Nothing happens automatically. Your trial simply ends after 7 days and you are never charged again unless you choose to unlock lifetime access yourself (one payment, your $9 applies). No subscription, nothing to cancel."
  },
  {
    question: "Will I be charged automatically after the trial?",
    answer: "No. We never charge automatically. We email you during your trial to remind you of that and to invite you to unlock lifetime access if you want to keep Dea — the choice is always yours."
  },
  {
    question: "How do I cancel?",
    answer: "There is nothing to cancel. The trial is a one-time $9 with no automatic charge after it, and lifetime access is a one-time payment. Questions? Reply to any email from Dea and a real person answers."
  },
  {
    question: "Is this really different from ChatGPT?",
    answer: "YES. Dea is trained on Deon's 20+ years of coaching. Try her for 7 days and feel the difference."
  }
];

// Features for "Why It's Different" section
const features = [
  {
    icon: Target,
    title: 'Your 90-Day Glow Up Plan',
    description: 'A personalized roadmap built around your goals, your life, and who you\'re becoming — not a generic template.'
  },
  {
    icon: Calendar,
    title: 'Daily Clarity & Direction',
    description: 'Every day Dea tells you exactly what to focus on — so you never wake up wondering where to start.'
  },
  {
    icon: Sparkles,
    title: 'Cycle Syncing',
    description: 'Work with your body, not against it. Dea adjusts your plan around your natural rhythm so you stay consistent without burning out.'
  },
  {
    icon: MessageCircle,
    title: 'A Coach Who Shows Up Daily',
    description: 'Dea is trained on 20+ years of real coaching wisdom — available 24/7, judgment-free, and always in your corner.'
  }
];

// FAQ Accordion Item Component
function FAQItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="border-b border-[#EAD8C2]">
      <button
        onClick={onClick}
        className="w-full py-5 flex items-center justify-between text-left hover:bg-[#EAD8C2]/10 transition-colors px-4 rounded-lg"
      >
        <span className="font-medium text-[#726158] pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-[#AA9283] flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-[#AA9283] flex-shrink-0" />
        )}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="pb-5 px-4"
          >
            <p className="text-[#AA9283]">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Interactive Quiz Section
function QuizSection({ scrollToCheckout }) {
  const [checkedItems, setCheckedItems] = useState({});
  const hasAnyChecked = Object.values(checkedItems).some(Boolean);
  
  const toggleItem = (index) => {
    setCheckedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  
  return (
    <section className="py-20 bg-[#FFFCF7]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="font-serif text-3xl sm:text-4xl text-[#726158] mb-3">
            Is the 7-Day Glow Up <span className="text-[#CAAB84]">For You?</span>
          </h2>
          <p className="text-[#AA9283] text-lg">Select all that apply:</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="space-y-3"
        >
          {quizItems.map((item, index) => (
            <button
              key={index}
              onClick={() => toggleItem(index)}
              className={`w-full flex items-center gap-4 p-4 min-h-[64px] rounded-xl border-2 transition-all text-left text-[13px] sm:text-base leading-relaxed ${
                checkedItems[index]
                  ? 'border-[#CAAB84] bg-[#CAAB84]/10'
                  : 'border-[#EAD8C2] hover:border-[#CAAB84]/50 bg-white'
              }`}
              data-testid={`quiz-item-${index}`}
            >
              <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                checkedItems[index]
                  ? 'border-[#CAAB84] bg-[#CAAB84]'
                  : 'border-[#AA9283]'
              }`}>
                {checkedItems[index] && (
                  <CheckCircle className="h-4 w-4 text-white" />
                )}
              </div>
              <span className="text-[#726158]">{item}</span>
            </button>
          ))}
        </motion.div>
        
        <AnimatePresence>
          {hasAnyChecked && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="text-center mt-8"
            >
              <Button 
                onClick={scrollToCheckout}
                className="btn-gold text-lg px-8 py-6"
                data-testid="quiz-cta-btn"
              >
                Yes! This Is Me - Start Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
        
        {!hasAnyChecked && (
          <p className="text-center text-[#B1897C] mt-6 text-sm">Select any that apply</p>
        )}
      </div>
    </section>
  );
}

export default function PowerStart() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [loading, setLoading] = useState(false);
  const checkoutRef = useRef(null);

  const scrollToCheckout = () => {
    // Route to the $9 → 7-day trial → $37/month flow (same used by /glow-up-gap funnel).
    // The checkout page handles the inline email/first_name gate for anonymous users.
    navigate('/checkout?plan=glow-up-trial');
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      navigate('/checkout?plan=glow-up-trial');
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO 
        title="Try Dea for $9 — 7-Day Trial | Dea"
        description="Dea is an AI Ambition Coach for Women of Faith. Meet Dea — your personal coach for clarity, habits, mindset, strategy, and well-being. $9 for 7 days, then $37/month if not cancelled."
      />
      {/* Scoped styles for hero chat typing indicator (PowerStart only) */}
      <style>{`
        .hero-typing-dot {
          width: 6px;
          height: 6px;
          border-radius: 9999px;
          background-color: rgba(255, 252, 247, 0.55);
          animation: heroTypingBounce 1.4s infinite ease-in-out both;
        }
        .hero-typing-dot:nth-child(1) { animation-delay: -0.32s; }
        .hero-typing-dot:nth-child(2) { animation-delay: -0.16s; }
        @keyframes heroTypingBounce {
          0%, 80%, 100% { transform: scale(0); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      
      <div className="min-h-screen bg-[#FFFCF7] overflow-x-hidden">
        {/* Navigation - PowerStart specific */}
        <nav className="sticky top-0 z-50 bg-[#FFFCF7]/95 backdrop-blur-lg border-b border-[#EAD8C2]/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/powerstart" className="flex items-center gap-2">
              <img src={LOGO_NAV} alt="Dea" className="h-10 w-auto" />
            </Link>
            <div className="flex items-center gap-4">
              {user ? (
                <Link to="/dashboard">
                  <Button className="btn-gold" data-testid="nav-dashboard-btn">
                    Go to Dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login?redirect=/powerstart">
                    <Button variant="ghost" className="text-[#AA9283] hover:text-[#726158]" data-testid="nav-login-btn">
                      Login
                    </Button>
                  </Link>
                  <Button onClick={scrollToCheckout} className="btn-gold" data-testid="nav-start-btn">
                    Try for $9
                  </Button>
                </>
              )}
            </div>
          </div>
        </nav>

        {/* SECTION 1: Hero — split layout (text left, chat card right) */}
        <section className="pt-12 pb-14 sm:pt-16 sm:pb-16 bg-[#EAD8C2]/30 overflow-hidden">
          <div className="max-w-7xl mx-auto px-5 sm:px-6">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              {/* LEFT: Text + CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="min-w-0 text-center lg:text-left"
              >
                <p
                  className="uppercase tracking-[0.16em] mb-5 font-medium"
                  style={{ color: '#B8956A', fontSize: '11px' }}
                  data-testid="hero-eyebrow"
                >
                  AI Glow Up Coach for Women of Faith
                </p>

                <h1
                  className="font-serif leading-[1.05] mb-7"
                  style={{ color: '#3D3229' }}
                  data-testid="hero-headline"
                >
                  <span className="block text-[34px] sm:text-5xl lg:text-6xl">Stop winging it.</span>
                  <span className="block text-[34px] sm:text-5xl lg:text-6xl">
                    Start becoming <span style={{ color: '#B8956A' }}>HER.</span>
                  </span>
                </h1>

                {/* Social proof: avatars + line */}
                <div className="flex items-center justify-center lg:justify-start gap-3 mb-6">
                  <div className="flex -space-x-2">
                    {['C', 'E', 'T', 'R'].map((initial, i) => (
                      <div
                        key={initial}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold ring-2 ring-[#FFFCF7]"
                        style={{
                          backgroundColor: '#B8956A',
                          fontSize: '11px',
                          zIndex: 4 - i
                        }}
                      >
                        {initial}
                      </div>
                    ))}
                  </div>
                  <p
                    className="text-[#AA9283] leading-snug max-w-[260px] text-left"
                    style={{ fontSize: '12px' }}
                  >
                    Hundreds of ambitious women are already glowing up with Dea
                  </p>
                </div>

                <p
                  className="text-[#6B5D54] mb-8 max-w-[520px] mx-auto lg:mx-0"
                  style={{ fontSize: '13px', lineHeight: 1.7 }}
                >
                  Dea is your personal AI ambition coach — she knows your goals, builds your plan, and checks in with you every single day. Try her for 7 days for just $9. If you love her (and you will), your membership continues at $37/month. Cancel anytime.
                </p>

                <button
                  onClick={scrollToCheckout}
                  data-testid="hero-cta-btn"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-7 py-4 transition-all hover:opacity-90 hover:-translate-y-0.5 shadow-md"
                  style={{
                    backgroundColor: '#3D3229',
                    color: '#FFFCF7',
                    borderRadius: '30px',
                    fontSize: '15px',
                    fontWeight: 500,
                    minHeight: '52px',
                    width: '100%',
                    maxWidth: '420px'
                  }}
                >
                  Try Dea for $9 — 7 Days Full Access
                  <ArrowRight className="ml-2 h-4 w-4 shrink-0" />
                </button>

                <div className="mt-4">
                  <a
                    href="https://clarity-to-glow.emergent.host/challenge"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="hero-free-challenge-link"
                    className="inline-block underline hover:opacity-80 transition-opacity"
                    style={{ color: '#B8956A', fontSize: '13px' }}
                  >
                    Try the free 5-day challenge first →
                  </a>
                </div>

                <p
                  className="mt-5 text-[#B1897C] max-w-[420px] mx-auto lg:mx-0"
                  style={{ fontSize: '11px', lineHeight: 1.6 }}
                  data-testid="hero-fine-print"
                >
                  $9 today · 7 days full access · No automatic charge after
                </p>
              </motion.div>

              {/* RIGHT: Chat preview card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="min-w-0 w-full"
                data-testid="hero-chat-preview"
              >
                <div
                  className="rounded-3xl shadow-2xl overflow-hidden mx-auto"
                  style={{
                    backgroundColor: '#3D3229',
                    padding: '1.5rem',
                    maxWidth: '440px'
                  }}
                >
                  {/* Chat header */}
                  <div className="flex items-center gap-3 pb-4 mb-4 border-b border-white/10">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0"
                      style={{ backgroundColor: '#B8956A', fontSize: '14px' }}
                    >
                      D
                    </div>
                    <div className="min-w-0">
                      <p
                        className="text-white"
                        style={{ fontSize: '13px', fontWeight: 500, lineHeight: 1.2 }}
                      >
                        Dea
                      </p>
                      <p style={{ color: 'rgba(255,252,247,0.55)', fontSize: '11px', marginTop: '2px' }}>
                        Your AI Glow Up Coach
                      </p>
                    </div>
                  </div>

                  {/* Chat messages — compact, scrollable on small screens */}
                  <div
                    className="space-y-3 overflow-y-auto pr-1 max-h-[280px] lg:max-h-none"
                    style={{ scrollbarWidth: 'thin' }}
                  >
                    {/* Dea bubble 1 */}
                    <motion.div
                      className="flex justify-start"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6, duration: 0.4, ease: 'easeOut' }}
                    >
                      <div
                        className="rounded-2xl rounded-tl-sm px-4 py-3 max-w-[88%]"
                        style={{
                          backgroundColor: 'rgba(255,252,247,0.08)',
                          color: '#F4EBE1',
                          fontSize: '13px',
                          lineHeight: 1.55
                        }}
                      >
                        Hey sis. Before we build your plan — what area of your life feels most stuck right now?
                      </div>
                    </motion.div>

                    {/* User bubble */}
                    <motion.div
                      className="flex justify-end"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.9, duration: 0.4, ease: 'easeOut' }}
                    >
                      <div
                        className="rounded-2xl rounded-tr-sm px-4 py-3 max-w-[88%] text-white"
                        style={{
                          backgroundColor: '#B8956A',
                          fontSize: '13px',
                          lineHeight: 1.55
                        }}
                      >
                        Honestly... all of it. My habits, my mindset, my business.
                      </div>
                    </motion.div>

                    {/* Dea bubble 2 */}
                    <motion.div
                      className="flex justify-start"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 3.3, duration: 0.4, ease: 'easeOut' }}
                    >
                      <div
                        className="rounded-2xl rounded-tl-sm px-4 py-3 max-w-[88%]"
                        style={{
                          backgroundColor: 'rgba(255,252,247,0.08)',
                          color: '#F4EBE1',
                          fontSize: '13px',
                          lineHeight: 1.55
                        }}
                      >
                        That's exactly why I'm here. Let's start with clarity and build from there. You're not behind — you're just about to begin. 💛
                      </div>
                    </motion.div>

                    {/* Typing indicator (loops after sequence to feel alive) */}
                    <motion.div
                      className="flex justify-start"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 4.6, duration: 0.4, ease: 'easeOut' }}
                      data-testid="hero-typing-indicator"
                    >
                      <div
                        className="rounded-2xl rounded-tl-sm flex items-center gap-1.5 px-4"
                        style={{ backgroundColor: 'rgba(255,252,247,0.08)', height: '36px' }}
                      >
                        <span className="hero-typing-dot" />
                        <span className="hero-typing-dot" />
                        <span className="hero-typing-dot" />
                      </div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SECTION 2: Testimonials — moved directly after hero */}
        <section className="py-14 sm:py-20 bg-[#EAD8C2]/30">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10 sm:mb-12"
            >
              <h2 className="font-serif text-3xl sm:text-4xl text-[#726158] mb-4">
                Real Results in <span className="text-[#CAAB84]">7 Days</span>
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-3 md:gap-6">
              {testimonials.map((testimonial, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="glass-card rounded-2xl p-6"
                >
                  <div className="flex mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Sparkles key={star} className="h-4 w-4 text-[#CAAB84]" />
                    ))}
                  </div>
                  <p className="text-[#AA9283] mb-4 italic text-sm">"{testimonial.quote}"</p>
                  <p className="text-[#726158] font-medium">— {testimonial.author}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 3: Is This For You? — Interactive Quiz */}
        <QuizSection scrollToCheckout={scrollToCheckout} />

        {/* SECTION 4: What You Get - Day by Day */}
        <section className="py-20 bg-[#FFFCF7]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-serif text-3xl sm:text-4xl text-[#726158] mb-4">
                Here's What Changes in <span className="text-[#CAAB84]">7 Days</span>
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {dayCards.map((card, i) => (
                <motion.div
                  key={card.day}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="glass-card rounded-2xl p-6 card-hover"
                >
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${card.color}20` }}
                  >
                    <card.icon className="h-7 w-7" style={{ color: card.color }} />
                  </div>
                  <div className="text-sm font-medium text-[#AA9283] mb-2">DAY {card.day}</div>
                  <h3 className="font-serif text-xl text-[#726158] mb-4">{card.title}</h3>
                  <ul className="space-y-2">
                    {card.points.map((point, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-[#AA9283]">
                        <span className="text-[#87A6A7] mt-1">•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mt-10"
            >
              <Button 
                onClick={scrollToCheckout}
                className="btn-gold text-lg px-8 py-6"
                data-testid="days-cta-btn"
              >
                I'm Ready — $9 for 7 Days
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: Meet Dea */}
        <section className="py-16 bg-[#EAD8C2]/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Left - Dea Chat Interface Mockup */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="bg-white rounded-2xl shadow-xl p-4 max-w-md mx-auto border border-[#EAD8C2]">
                  {/* Chat Header */}
                  <div className="flex items-center gap-3 pb-4 border-b border-[#EAD8C2]">
                    <img src={DEA_AVATAR} alt="Dea" className="w-10 h-10 rounded-full object-cover object-top" />
                    <div>
                      <p className="font-serif text-[#726158]">Dea</p>
                      <p className="text-xs text-[#87A6A7]">Your AI Glow Up Coach</p>
                    </div>
                  </div>
                  
                  {/* Chat Messages */}
                  <div className="py-4 space-y-4">
                    <div className="flex gap-3">
                      <img src={DEA_AVATAR} alt="Dea" className="w-8 h-8 rounded-full object-cover object-top flex-shrink-0" />
                      <div className="bg-[#EAD8C2]/30 rounded-2xl rounded-tl-none p-3 max-w-[85%]">
                        <p className="text-sm text-[#726158]">Hey sis! I'm Dea — your AI glow up coach. I'm here for ALL of you. Your goals, your mindset, your habits, your strategy, your well-being. Let's figure out where your glow up starts. What area of your life are we working on first?</p>
                      </div>
                    </div>
                    <div className="flex gap-3 justify-end">
                      <div className="bg-[#CAAB84]/20 rounded-2xl rounded-tr-none p-3 max-w-[85%]">
                        <p className="text-sm text-[#726158]">I want to feel more like myself again — confident, clear, and actually making progress.</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <img src={DEA_AVATAR} alt="Dea" className="w-8 h-8 rounded-full object-cover object-top flex-shrink-0" />
                      <div className="bg-[#EAD8C2]/30 rounded-2xl rounded-tl-none p-3 max-w-[85%]">
                        <p className="text-sm text-[#726158]">Yes. That's exactly what we're building. Let me create your personalized glow up plan — your habits, your mindset, your strategy, all of it. Starting today.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Right - Content */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="space-y-5 mt-8 md:mt-0"
              >
                <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#726158]">
                  Meet <span className="text-[#CAAB84]">Dea</span>, Your AI Glow Up Coach
                </h2>
                <p className="text-[#AA9283]">
                  Dea is trained by Deon Stokes — coach, founder, and the woman behind The Ambition Brand. With 20+ years of experience helping women transform their goals, habits, mindset, strategy, and well-being, Deon poured everything she knows into Dea. So that you can have a coach in your corner every single day — not just when you can afford one.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#87A6A7] flex-shrink-0 mt-0.5" />
                    <span className="text-[#AA9283]">Clarity on your goals so you stop spinning and finally start moving</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#87A6A7] flex-shrink-0 mt-0.5" />
                    <span className="text-[#AA9283]">Daily habits and mindset support that make your glow up feel sustainable</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#87A6A7] flex-shrink-0 mt-0.5" />
                    <span className="text-[#AA9283]">Business strategy for when you're ready to grow your income and your impact</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#87A6A7] flex-shrink-0 mt-0.5" />
                    <span className="text-[#AA9283]">Cycle syncing to work WITH your body — not against it</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-[#87A6A7] flex-shrink-0 mt-0.5" />
                    <span className="text-[#AA9283]">24/7 accountability from a coach who shows up every single day</span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SECTION 4: Why It's Different - Features */}
        <section className="py-20 bg-[#FFFCF7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-serif text-3xl sm:text-4xl text-[#726158] mb-4">
                Why It's <span className="text-[#CAAB84]">Different</span>
              </h2>
              <p className="text-[#AA9283] max-w-2xl mx-auto">
                ChatGPT can give you a plan. Dea helps you actually become <strong className="text-[#726158]">HER</strong>.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, i) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="glass-card rounded-2xl p-6 card-hover"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#87A6A7]/15 flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-[#87A6A7]" />
                  </div>
                  <h3 className="font-serif text-xl text-[#726158] mb-2">{feature.title}</h3>
                  <p className="text-[#AA9283] text-sm">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 7: Meet Deon */}
        <section className="py-16 bg-[#FFFCF7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Left - Content */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="space-y-5 order-2 md:order-1"
              >
                <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#726158] leading-tight">
                  Meet <span className="text-[#CAAB84]">Deon, MBA</span> — The Face Of Ambition
                </h2>
                <div className="space-y-4 text-[#AA9283]">
                  <p>
                    For 20 years, I've coached ambitious women through every season of life — building businesses, rebuilding themselves, finding clarity, and becoming the woman they always knew they were meant to be.
                  </p>
                  <p>
                    And the #1 thing I heard over and over?
                  </p>
                  <p className="text-[#726158] font-medium italic text-lg">
                    "I just wish someone could help me figure out ALL of it — not just my business, but my habits, my mindset, my goals, and how to actually feel good while I'm doing it."
                  </p>
                  <p>
                    I wanted to be that coach. The one who shows up for your goals AND your well-being. The one who helps you glow up in every area of your life — not just the ones that look good on paper.
                  </p>
                  <p>
                    But I couldn't show up for everyone, every single day.
                  </p>
                  <p className="font-medium text-[#726158]">
                    So I built Dea.
                  </p>
                  <p>
                    She's the glow up coach I always wished I could be — trained on everything I know, available every single day, and built entirely for YOU.
                  </p>
                </div>
              </motion.div>

              {/* Right - Image */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="relative order-1 md:order-2"
              >
                <div className="rounded-3xl shadow-xl w-full max-w-md mx-auto overflow-hidden">
                  <img 
                    src={DEON_AVATAR} 
                    alt="Deon Stokes, MBA - The Face of Ambition" 
                    className="w-full h-auto object-cover"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* SECTION 8: What's Included with Your Membership */}
        <section className="py-20 bg-[#EAD8C2]/30">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-serif text-3xl sm:text-4xl text-[#726158] mb-4">
                What{"\u2019"}s Included in Your <span className="text-[#CAAB84]">7-Day Trial</span>
              </h2>
              <p className="text-[#AA9283]">
                Everything Dea brings to your day {"\u2014"} $9 for 7 days, no automatic charge after.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card rounded-2xl p-6 sm:p-8 border-2 border-[#CAAB84] relative overflow-hidden"
            >
              <div className="text-center mb-6">
                <Zap className="h-10 w-10 text-[#CAAB84] mx-auto mb-3" />
                <h3 className="font-serif text-2xl text-[#726158]">7-Day Trial with Dea</h3>
                <p className="text-4xl font-serif text-[#CAAB84] mt-2">$9<span className="text-xl text-[#AA9283]"> today</span></p>
                <p className="text-sm text-[#AA9283] mt-1">No automatic charge after your 7 days \u00b7 Unlock lifetime access only if you choose</p>
              </div>
              <ul className="space-y-3 max-w-lg mx-auto">
                {[
                  "Full access to Dea \u2014 your AI Glow Up Coach",
                  "Your personalized 90-day glow up plan",
                  "Daily clarity, habits, and accountability",
                  "Mindset coaching that meets you where you are",
                  "Business strategy when you\u2019re ready to grow",
                  "Cycle syncing to work with your body",
                  "Weekly & monthly reflections",
                  "Progress tracking across every pillar",
                  "No surprise charges — ever"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#726158]">
                    <CheckCircle className="h-5 w-5 text-[#CAAB84] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-center text-[#87A6A7] text-sm mt-6 font-medium">(One payment for life if you stay. No subscription, nothing to cancel.)</p>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mt-10 space-y-4"
            >
              <p className="text-[#726158] max-w-lg mx-auto">
                <strong>$9 for 7 days. Nothing charged automatically after.</strong> No contracts. No phone calls. No hoops. Just a coach who actually shows up for you.
              </p>
              <Button 
                onClick={scrollToCheckout}
                className="btn-gold text-lg px-8 py-6"
                data-testid="comparison-cta-btn"
              >
                Try Dea for 7 Days {"\u2014"} $9
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* SECTION 9: FAQs - Accordion */}
        <section className="py-20 bg-[#FFFCF7]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <h2 className="font-serif text-3xl sm:text-4xl text-[#726158]">
                Questions?
              </h2>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card rounded-2xl overflow-hidden"
            >
              {faqItems.map((faq, i) => (
                <FAQItem
                  key={i}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openFaq === i}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                />
              ))}
            </motion.div>
          </div>
        </section>

        {/* SECTION 10: Final CTA & Checkout */}
        <section className="py-20 bg-[#EAD8C2]/30">
          <div className="max-w-lg mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h2 className="font-serif text-3xl sm:text-4xl text-[#726158]">
                Ready to Start Your <span className="text-[#CAAB84]">Glow Up?</span>
              </h2>
              <p className="text-[#AA9283] mt-4">
                She's already built your plan. She's already ready for you. The only thing missing is you.
              </p>
            </motion.div>

            <motion.div
              ref={checkoutRef}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card rounded-2xl p-6 sm:p-8 border-2 border-[#CAAB84]/30 transition-all duration-300"
              data-testid="checkout-card"
            >
              <div className="text-center mb-6">
                <h3 className="font-serif text-2xl text-[#726158] mb-2">Start Your 7-Day Trial</h3>
                <p className="text-4xl font-serif text-[#CAAB84]">$9<span className="text-xl text-[#AA9283]"> today</span></p>
                <p className="text-sm text-[#AA9283]">No automatic charge after your trial</p>
              </div>

              <ul className="space-y-3 mb-8">
                {[
                  "Full access to Dea — your AI glow up coach",
                  "Glow Up Clarity Sequence on Day 1",
                  "Your personalized 90-day glow up plan",
                  "Daily direction so you always know what to do next",
                  "Keep Dea for life only if you choose — one payment, no subscription"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#726158]">
                    <CheckCircle className="h-5 w-5 text-[#87A6A7] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>

              <Button 
                onClick={handleCheckout}
                disabled={loading}
                className="w-full btn-gold text-lg py-6"
                data-testid="checkout-btn"
              >
                {loading ? (
                  <>
                    <span className="spinner mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    Start My 7-Day Trial — $9
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>

              <p className="text-center text-[#B1897C] text-xs italic mt-4">
                7 days full access. No automatic charge after. You're in control.
              </p>
              <div className="flex items-center justify-center gap-4 mt-2 text-xs text-[#B1897C]">
                <span className="flex items-center gap-1">
                  <Lock className="h-3 w-3" />
                  Secure checkout
                </span>
                <span>•</span>
                <span>All major cards accepted</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer - PowerStart specific - matches main site */}
        <footer className="py-12 border-t border-[#EAD8C2] bg-[#FFFCF7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex flex-col items-center md:items-start gap-1">
                <img src={LOGO_HERO} alt="Dea" className="h-12 w-auto" />
              </div>
              <div className="flex items-center gap-4">
                <Link to="/privacy" className="text-[#B1897C] text-sm hover:text-[#726158] transition-colors">
                  Privacy Policy
                </Link>
                <span className="text-[#B1897C]">|</span>
                <Link to="/terms" className="text-[#B1897C] text-sm hover:text-[#726158] transition-colors">
                  Terms of Service
                </Link>
                <span className="text-[#B1897C]">|</span>
                <p className="text-[#B1897C] text-sm">
                  © 2026 The Ambition Brand. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
